<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useMessageContext } from '../../provider.js';
import { MESSAGE_TYPES } from '../../constants';

const BUTTON_PATTERN =
  /^[\s]*[▸►•▪▫▹▻●○◆◇➤➜→⮞🔹🔸]\s*(.+)$/;
const NUMBERED_PATTERN = /^[\s]*(\d+)[.)]\s*(.+)$/;

const { content, conversationId, messageType } = useMessageContext();
const store = useStore();
const { t } = useI18n();

const isIncoming = computed(
  () => messageType.value === MESSAGE_TYPES.INCOMING
);

const parsed = computed(() => {
  if (!content.value || !isIncoming.value) return { header: '', buttons: [] };

  const lines = content.value.split('\n');
  const headerLines = [];
  const buttons = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (buttons.length === 0) headerLines.push('');
      continue;
    }

    const bulletMatch = trimmed.match(BUTTON_PATTERN);
    const numberMatch = trimmed.match(NUMBERED_PATTERN);

    if (bulletMatch) {
      buttons.push(bulletMatch[1].trim());
    } else if (numberMatch) {
      buttons.push(numberMatch[2].trim());
    } else if (buttons.length === 0) {
      headerLines.push(line);
    }
  }

  // Only treat as interactive if we found 2+ buttons
  if (buttons.length < 2) return { header: '', buttons: [] };

  return {
    header: headerLines.join('\n').trim(),
    buttons,
  };
});

const hasButtons = computed(() => parsed.value.buttons.length >= 2);

async function sendReply(buttonText) {
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: conversationId.value,
      message: buttonText,
      private: false,
    });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    emitter.emit(BUS_EVENTS.MESSAGE_SENT);
  } catch {
    useAlert(t('CONVERSATION.MESSAGE_ERROR'));
  }
}
</script>

<template>
  <div v-if="hasButtons" class="flex flex-col gap-1.5 mt-2">
    <button
      v-for="(btn, idx) in parsed.buttons"
      :key="idx"
      class="w-full px-3 py-2 text-sm text-left rounded-lg border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-3 active:bg-n-alpha-4 text-n-slate-12 transition-colors cursor-pointer"
      @click="sendReply(btn)"
    >
      {{ btn }}
    </button>
  </div>
</template>
